import { useEffect, useRef } from 'react';

export type ToastTimer = {
  /**
   * How long the toast survives once running, in milliseconds.
   */
  duration: number;
  id: number;
  /**
   * Whether this one toast is held while the rest keep running — a buried card has not been seen yet.
   */
  isHeld?: boolean;
};

type ToastCountdowns = {
  /**
   * How long each toast still has to run, in milliseconds.
   */
  remaining: Map<number, number>;
  /**
   * When each running countdown was last started. Absent while held.
   */
  startedAt: Map<number, number>;
  /**
   * The live timeout behind each running countdown. Its presence is the record of a countdown running.
   */
  timeouts: Map<number, ReturnType<typeof setTimeout>>;
};

type ToastTimersOptions = {
  /**
   * Whether every countdown is held, whatever each toast asks for individually.
   */
  isPaused: boolean;
  /**
   * Runs when a countdown reaches zero. Must be stable, or held time is re-read on every render.
   */
  onExpire: (id: number) => void;
  /**
   * The open toasts and their delays. A closing toast belongs out of this list.
   */
  timers: ToastTimer[];
};

const forgetDepartedCountdowns = ({ remaining, startedAt, timeouts }: ToastCountdowns, liveIds: Set<number>) => {
  for (const id of remaining.keys()) {
    if (liveIds.has(id)) {
      continue;
    }

    const timeout = timeouts.get(id);
    if (timeout !== undefined) {
      clearTimeout(timeout);
      timeouts.delete(id);
    }

    remaining.delete(id);
    startedAt.delete(id);
  }
};

/**
 * Stops running countdowns, banking what each toast had left.
 *
 * Walks the timeouts rather than the toasts: an entry exists there only while a countdown is running,
 * which keeps a render mid-hold from charging the same elapsed time twice.
 * @param countdowns - the bookkeeping to hold
 * @param heldIds - which notifications to hold; every running one when omitted
 */
const holdCountdowns = ({ remaining, startedAt, timeouts }: ToastCountdowns, heldIds?: Set<number>) => {
  for (const [id, timeout] of timeouts) {
    if (heldIds && !heldIds.has(id)) {
      continue;
    }

    clearTimeout(timeout);

    const startTime = startedAt.get(id);
    const elapsed = startTime === undefined ? 0 : Date.now() - startTime;

    remaining.set(id, (remaining.get(id) ?? 0) - elapsed);
    startedAt.delete(id);
    timeouts.delete(id);
  }
};

/**
 * Starts a countdown for every listed toast not already running. Skipping the running ones stops an
 * arriving notification from handing its siblings their full duration back.
 * @param countdowns - the bookkeeping to run
 * @param timers - the toasts that should be counting down
 * @param onExpire - runs when a countdown reaches zero
 */
const startCountdowns = (
  { remaining, startedAt, timeouts }: ToastCountdowns,
  timers: ToastTimer[],
  onExpire: (id: number) => void
) => {
  for (const { id } of timers) {
    if (timeouts.has(id)) {
      continue;
    }

    const delay = remaining.get(id) ?? 0;

    /*
     * A toast asking never to dismiss itself. `setTimeout` would do the opposite: it coerces the delay
     * to a 32-bit integer, so a non-finite one becomes 0 and the toast goes on the next tick.
     */
    if (!Number.isFinite(delay)) {
      continue;
    }

    startedAt.set(id, Date.now());
    timeouts.set(
      id,
      setTimeout(
        () => {
          timeouts.delete(id);
          startedAt.delete(id);
          onExpire(id);
        },
        Math.max(delay, 0)
      )
    );
  }
};

/**
 * Counts each live toast down to its dismissal, holding every countdown while `isPaused` and any
 * individually held one the rest of the time.
 *
 * Owned here rather than left to Radix, which decides for itself when to hold — on `focusin`, window
 * blur and hover, none of it configurable. Radix's own countdown must be off for this one to be the
 * only one: `duration={Infinity}` on its provider, and no `duration` on a toast.
 * @param options - the live toasts, whether to hold them, and what to do when one elapses
 */
const useToastTimers = ({ isPaused, onExpire, timers }: ToastTimersOptions): void => {
  const countdownsRef = useRef<ToastCountdowns>({ remaining: new Map(), startedAt: new Map(), timeouts: new Map() });

  useEffect(() => {
    const { timeouts } = countdownsRef.current;

    return () => {
      for (const timeout of timeouts.values()) {
        clearTimeout(timeout);
      }
      timeouts.clear();
    };
  }, []);

  /*
   * Reconciles the countdowns with the toasts on screen. Deliberately without a cleanup clearing the
   * timeouts: this re-runs whenever the stack changes, which would restart every surviving toast.
   */
  useEffect(() => {
    const countdowns = countdownsRef.current;

    forgetDepartedCountdowns(countdowns, new Set(timers.map(({ id }) => id)));

    for (const { duration, id } of timers) {
      if (!countdowns.remaining.has(id)) {
        countdowns.remaining.set(id, duration);
      }
    }

    if (isPaused) {
      holdCountdowns(countdowns);

      return;
    }

    const heldIds = new Set(timers.filter(({ isHeld }) => isHeld).map(({ id }) => id));

    holdCountdowns(countdowns, heldIds);
    startCountdowns(
      countdowns,
      timers.filter(({ id }) => !heldIds.has(id)),
      onExpire
    );
  }, [isPaused, onExpire, timers]);
};

export default useToastTimers;
